/**
 * The distance and travel time shown on the booking map.
 *
 * Both are ESTIMATES and are written to read as estimates. The legacy booking page showed an exact
 * figure ("22 mins"), which invites being treated as a promise — the driver is then late against a
 * number nobody guaranteed. A range says what we actually know.
 */

/** Round to whole kilometres, as the legacy page did (`Math.round(journey.distance) + 'KM'`). */
export const formatDistance = (metres: number): string => `${Math.round(metres / 1000)} KM`;

/**
 * How wide the band is, in minutes.
 *
 * A five-minute band on a two-hour airport run would read "120-125 mins" — precision we plainly do
 * not have, and the longer the trip the more traffic can move it. So the band widens with the trip.
 */
const bandMinutes = (minutes: number): number => (minutes < 60 ? 5 : 15);

/**
 * A travel time as a range: the estimate rounded DOWN to the band, and one band above it.
 *
 * Always a range, never a point. Rounding down and up to the nearest band would collapse on exact
 * multiples — a 20-minute trip becoming "20-20 mins", which reads as broken and loses the whole
 * reason for the range.
 *
 * Under an hour it counts minutes ("20-25 mins"); at an hour and over it switches to hours, because
 * "125-140 mins" is arithmetic the reader has to do themselves.
 */
export const formatDuration = (seconds: number): string => {
  const minutes = Math.round(seconds / 60);
  const band = bandMinutes(minutes);
  const lower = Math.floor(minutes / band) * band;
  const upper = lower + band;

  /*
   * No trip takes zero minutes. Flooring a three-minute hop gives a lower bound of 0, and "0-5 mins"
   * reads as a glitch rather than an estimate — so the shortest trips get an upper bound only.
   */
  if (lower === 0) return `Under ${upper} mins`;

  if (upper < 60) return `${lower}-${upper} mins`;

  return `${asHours(lower)} - ${asHours(upper)}`;
};

/** "2h", "1h 45m" — the minutes half is dropped when it is zero rather than shown as "2h 0m". */
const asHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours === 0) return `${rest}m`;

  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
};
